import clsx from 'clsx';
import ToggleIcon from '../../assets/icons/sidebar-toggle-icon.svg?react';
import React, { useState } from 'react';
import { toggleSwitch } from '../../reducers/switch';
import ChevronIcon from '../../assets/icons/chevron-down-icon.svg?react';
import CheckIcon from '../../assets/icons/check-green-icon.svg?react';
import { useDispatch } from 'react-redux';
import Button from '../../components/Button';
import { useButtonSizeSmall } from '../../utils/buttonSizes';
import DownloadIcon from '../../assets/icons/download-icon.svg?react';
import {
  InvestigationQuestionWithCategory,
  InvestigationAffectedBodyPartsEnum,
  Attachments,
  InvestigationReportingControllerFindAllInjuriesIncidentClassificationEnum,
  InvestigationReportingControllerFindAllInjuriesIncidentTypeOfLossEnum,
} from '@EHSNavigator/backend/dist/api';
import { Link } from 'react-router';
import moment from 'moment';

interface InvestigationReportingStepFiveProps {
  otherClasses?: string;
  formHandler: any;
  handleNextStep: (steps: number) => void;
  questions: InvestigationQuestionWithCategory[];
  attachments: Attachments[];
  disbaleButtons: boolean;
}

const InvestigationReportingStepFive: React.FC<
  InvestigationReportingStepFiveProps
> = ({
  otherClasses,
  formHandler,
  handleNextStep,
  questions,
  attachments,
  disbaleButtons,
}) => {
  const investigationReportingStepFiveClasses = clsx(otherClasses);
  const [toggle, setToggle] = useState('');
  const dispatch = useDispatch();
  const sm = useButtonSizeSmall();

  return (
    <div
      className={investigationReportingStepFiveClasses}
      data-testid="investigation-reporting-step-five"
    >
      <div className="flex flex-row items-start gap-2.5">
        <button
          type="button"
          className="bg-translucent-background/80 border-neutral-tones-text/30 rounded-5 w-fit cursor-pointer border p-0.5 lg:hidden"
          onClick={() => dispatch(toggleSwitch())}
        >
          <ToggleIcon />
        </button>
        <div className="flex flex-col gap-1">
          <span className="text-brand-byzantine font-inter-variable text-xs leading-4 font-bold lg:hidden">
            INJURY/ILLNESS
          </span>
          <h4 className="text-neutral-tones-text font-inter-variable font-bold">
            Review Your Responses
          </h4>
          <p className="text-brand-byzantine font-inter-variable text-base leading-5 font-bold lg:mt-15">
            STEP{' '}
            {formHandler?.values?.files?.length > 0
              ? 4
              : questions?.length > 0
                ? 5
                : 4}{' '}
            /{' '}
            {formHandler?.values?.files?.length > 0
              ? 4
              : questions?.length > 0
                ? 5
                : 4}
          </p>
        </div>
      </div>
      <p className="font-inter-variable text-neutral-tones-text mt-8 text-sm leading-4 font-medium lg:mt-9 lg:text-xl lg:leading-6">
        We kindly request that you meticulously review your responses provided
        below and ensure that all entries are accurately typed and appropriately
        articulated.
      </p>
      <div className="mt-9 flex flex-wrap gap-9">
        <div className="flex flex-col gap-5">
          <h6 className="text-brand-byzantine font-inter-variable text-base leading-5 font-bold lg:text-xl lg:leading-6">
            Injury or Illness
            <span className="text-brand-posh-pink">*</span>
          </h6>
          <p className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
            {formHandler?.values?.injuryIllness || 'N/A'}
          </p>
        </div>
        <div className="flex flex-col gap-5">
          <h6 className="text-brand-byzantine font-inter-variable text-base leading-5 font-bold lg:text-xl lg:leading-6">
            Type of Loss
            <span className="text-brand-posh-pink">*</span>
          </h6>
          <p className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
            {formHandler?.values?.typeOfLoss || 'N/A'}
          </p>
        </div>
        <div className="flex flex-col gap-5">
          <h6 className="text-brand-byzantine font-inter-variable text-base leading-5 font-bold lg:text-xl lg:leading-6">
            Classification
            <span className="text-brand-posh-pink">*</span>
          </h6>
          <p className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
            {formHandler?.values?.classification || 'N/A'}
          </p>
        </div>
        {formHandler?.values?.classifyIllness && (
          <div className="flex flex-col gap-5">
            <h6 className="text-brand-byzantine font-inter-variable text-base leading-5 font-bold lg:text-xl lg:leading-6">
              Classify Illness
              <span className="text-brand-posh-pink">*</span>
            </h6>
            <p className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              {formHandler?.values?.classifyIllness || 'N/A'}
            </p>
          </div>
        )}
        {formHandler?.values?.daysAwayFromWork && (
          <div className="flex flex-col gap-5">
            <h6 className="text-brand-byzantine font-inter-variable text-base leading-5 font-bold lg:text-xl lg:leading-6">
              Days Away From Work
              <span className="text-brand-posh-pink">*</span>
            </h6>
            <p className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              {formHandler?.values?.daysAwayFromWork || 'N/A'}
            </p>
          </div>
        )}
        {formHandler?.values?.jobRestrictionDays && (
          <div className="flex flex-col gap-5">
            <h6 className="text-brand-byzantine font-inter-variable text-base leading-5 font-bold lg:text-xl lg:leading-6">
              Job Restriction Days
              <span className="text-brand-posh-pink">*</span>
            </h6>
            <p className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              {formHandler?.values?.jobRestrictionDays || 'N/A'}
            </p>
          </div>
        )}
        {formHandler?.values?.dateOfDeath && (
          <div className="flex flex-col gap-5">
            <h6 className="text-brand-byzantine font-inter-variable text-base leading-5 font-bold lg:text-xl lg:leading-6">
              Date Of Death
              <span className="text-brand-posh-pink">*</span>
            </h6>
            <p className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
              {moment(formHandler?.values?.dateOfDeath).format('MMM DD YYYY')}
            </p>
          </div>
        )}
      </div>
      <div className="mt-9 flex flex-col">
        <h6 className="font-inter-variable text-brand-byzantine -tracking-button text-base leading-5 font-bold lg:text-xl lg:leading-6">
          Body Parts Affected
        </h6>
        <p className="font-inter-variable text-neutral-tones-text mt-1 text-base leading-5 font-normal">
          Body parts of the employee affected by the injury/illness due to the
          incident:
        </p>

        <div className="relative mt-5 flex max-w-545 flex-wrap gap-2.5">
          {Object?.values(InvestigationAffectedBodyPartsEnum)?.map(
            (node, index) => (
              <Button
                variant="primary"
                size="smaller"
                type="button"
                key={index}
                disable={!formHandler.values.bodyPartAffected?.includes(node)}
                otherClasses={clsx(
                  'w-121 hover:!bg-brand-byzantine/80 hover:!text-white disabled:!bg-additional-disable disabled:!text-neutral-tones-text/50',
                )}
              >
                {node}
              </Button>
            ),
          )}
          {formHandler?.touched.bodyPartAffected &&
            formHandler?.errors.bodyPartAffected && (
              <span className="font-inter-variable text-brand-posh-pink absolute -bottom-5 left-0 text-xs font-normal">
                {formHandler?.errors.bodyPartAffected}
              </span>
            )}
        </div>
      </div>

      {(formHandler?.values?.files || [])?.length === 0 &&
        attachments?.length === 0 &&
        formHandler?.values?.fiveWhysQuestionAnswer?.length > 0 && (
          <div className="border-neutral-tones-text/10 bg-card-backgrounds/5 rounded-10 mt-9 w-full border p-15 lg:p-5">
            <button
              type="button"
              onClick={() => setToggle(toggle === 'step-2' ? '' : 'step-2')}
              className="text-brand-papery flex w-full cursor-pointer items-center justify-between text-left text-base leading-5 font-bold uppercase lg:text-xl lg:leading-6"
            >
              5 WHY’s
              <ChevronIcon
                className={clsx(
                  'h-8 w-8 transition-all duration-300 lg:h-11 lg:w-11 [&>path]:fill-current',
                  toggle === 'step-2' && 'rotate-180',
                )}
              />
            </button>
            {toggle === 'step-2' && (
              <div
                className={clsx(
                  'mt-6 grid h-auto max-w-766 grid-cols-1 gap-x-6 gap-y-12 overflow-hidden transition-all duration-300 sm:grid-cols-2',
                )}
              >
                {formHandler?.values?.fiveWhysQuestionAnswer?.map(
                  (node: any, index: number) => (
                    <div key={index} className="flex flex-col gap-15">
                      <h6 className="text-brand-byzantine font-inter-variable text-base leading-5 font-normal lg:text-lg lg:leading-5">
                        {node?.question}
                      </h6>
                      <p className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal">
                        {node?.answer || 'N/A'}
                      </p>
                    </div>
                  ),
                )}
              </div>
            )}
          </div>
        )}
      {(formHandler?.values?.files || [])?.length === 0 &&
        attachments?.length === 0 &&
        questions?.length > 0 && (
          <div className="border-neutral-tones-text/10 bg-card-backgrounds/5 rounded-10 mt-9 w-full border p-15 lg:p-5">
            <button
              type="button"
              onClick={() => setToggle(toggle === 'step-3' ? '' : 'step-3')}
              className="text-brand-papery flex w-full cursor-pointer items-center justify-between text-left text-base leading-5 font-bold uppercase lg:text-xl lg:leading-6"
            >
              CUSTOM QUESTIONS
              <ChevronIcon
                className={clsx(
                  'h-8 w-8 transition-all duration-300 lg:h-11 lg:w-11 [&>path]:fill-current',
                  toggle === 'step-3' && 'rotate-180',
                )}
              />
            </button>
            {toggle === 'step-3' && (
              <div
                className={clsx(
                  'mt-6 grid h-auto grid-cols-1 gap-6 overflow-hidden transition-all duration-300',
                )}
              >
                {questions?.map((node: any, index: number) => (
                  <div
                    key={index}
                    className="border-b-neutral-tones-text/10 border-b pb-6 last:border-b-0"
                  >
                    <div className="flex flex-col gap-5">
                      <h6 className="text-neutral-tones-text/50 font-inter-variable text-base leading-5 font-bold uppercase lg:text-xl lg:leading-6">
                        {node?.category}
                      </h6>

                      <div
                        className={clsx(
                          'grid gap-x-6 gap-y-12',
                          node?.questions?.length === 2
                            ? 'grid-cols-1 md:grid-cols-2'
                            : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3',
                        )}
                      >
                        {node?.questions?.map(
                          (question: any, index: number) => (
                            <div key={index} className="flex flex-col gap-15">
                              <h6 className="text-brand-byzantine font-inter-variable text-base leading-5 font-normal capitalize lg:text-lg">
                                {question?.question}
                              </h6>
                              <p className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal capitalize">
                                {formHandler?.values?.customQuestions?.find(
                                  (q: any) => q.questionId === question?.id,
                                )?.answer || 'N/A'}
                              </p>
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      {(formHandler?.values?.files || [])?.length === 0 &&
        attachments?.length === 0 &&
        formHandler?.values?.otherQuestions?.length > 0 && (
          <div className="border-neutral-tones-text/10 bg-card-backgrounds/5 rounded-10 mt-9 w-full border p-15 lg:p-5">
            <button
              type="button"
              onClick={() =>
                setToggle(toggle === 'other-question' ? '' : 'other-question')
              }
              className="text-brand-papery flex w-full cursor-pointer items-center justify-between text-left text-base leading-5 font-bold uppercase lg:text-xl lg:leading-6"
            >
              OTHER QUESTIONS
              <ChevronIcon
                className={clsx(
                  'h-8 w-8 transition-all duration-300 lg:h-11 lg:w-11 [&>path]:fill-current',
                  toggle === 'other-question' && 'rotate-180',
                )}
              />
            </button>
            {toggle === 'other-question' && (
              <div
                className={clsx(
                  'mt-6 grid h-auto max-w-955 grid-cols-1 gap-x-6 gap-y-12 overflow-hidden transition-all duration-300 sm:grid-cols-2',
                )}
              >
                {formHandler?.values?.otherQuestions.length > 0 &&
                  formHandler?.values?.otherQuestions?.map(
                    (node: any, index: number) => (
                      <div key={index} className="flex flex-col gap-15">
                        <h6 className="text-brand-byzantine font-inter-variable text-base leading-5 font-normal capitalize lg:text-lg">
                          {node?.question}
                        </h6>
                        <p className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal capitalize">
                          {node?.answer || 'N/A'}
                        </p>
                      </div>
                    ),
                  )}
              </div>
            )}
          </div>
        )}
      <div className="border-neutral-tones-text/10 bg-card-backgrounds/5 rounded-10 mt-9 w-full border p-15 lg:p-5">
        <button
          type="button"
          onClick={() =>
            setToggle(toggle === 'other-context' ? '' : 'other-context')
          }
          className="text-brand-papery flex w-full cursor-pointer items-center justify-between text-left text-base leading-5 font-bold uppercase lg:text-xl lg:leading-6"
        >
          Incident Narative
          <ChevronIcon
            className={clsx(
              'h-8 w-8 transition-all duration-300 lg:h-11 lg:w-11 [&>path]:fill-current',
              toggle === 'other-context' && 'rotate-180',
            )}
          />
        </button>
        {toggle === 'other-context' && (
          <div
            className={clsx(
              'mt-6 grid h-auto max-w-955 grid-cols-1 gap-x-6 gap-y-12 overflow-hidden transition-all duration-300 sm:grid-cols-2',
            )}
          >
            <div className="flex flex-col gap-15">
              <h6 className="text-brand-byzantine font-inter-variable text-base leading-5 font-normal capitalize lg:text-lg">
                What was employee doing just before the incident occurred?
              </h6>
              <p className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal capitalize">
                {formHandler?.values?.narBeforeIncident || 'N/A'}
              </p>
            </div>
            <div className="flex flex-col gap-15">
              <h6 className="text-brand-byzantine font-inter-variable text-base leading-5 font-normal capitalize lg:text-lg">
                What happened? Tell us how the injury or illness occurred?
              </h6>
              <p className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal capitalize">
                {formHandler?.values?.narWhatHappened || 'N/A'}
              </p>
            </div>
            <div className="flex flex-col gap-15">
              <h6 className="text-brand-byzantine font-inter-variable text-base leading-5 font-normal capitalize lg:text-lg">
                What object or substance directly harmed the employee?
              </h6>
              <p className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal capitalize">
                {formHandler?.values?.narObjectSubstance || 'N/A'}
              </p>
            </div>
            {(formHandler?.values?.classification ===
              InvestigationReportingControllerFindAllInjuriesIncidentClassificationEnum.Hospitalized ||
              formHandler?.values?.typeOfLoss ===
                InvestigationReportingControllerFindAllInjuriesIncidentTypeOfLossEnum.MedicalTreatmentBeyondFirstAid) &&
              formHandler?.values?.treatmentFacilityType && (
                <div className="flex flex-col gap-15">
                  <h6 className="text-brand-byzantine font-inter-variable text-base leading-5 font-normal capitalize lg:text-lg">
                    Was employee treated in emergency room?
                  </h6>
                  <p className="text-neutral-tones-text font-inter-variable text-base leading-5 font-normal capitalize">
                    {formHandler?.values?.treatmentFacilityType || 'N/A'}
                  </p>
                </div>
              )}
          </div>
        )}
      </div>
      {((formHandler?.values?.files || [])?.length > 0 ||
        attachments?.length > 0) && (
        <div className="border-neutral-tones-text/10 bg-card-backgrounds/5 rounded-10 mt-9 w-full border p-15 lg:p-5">
          <button
            type="button"
            className="text-brand-papery flex w-full cursor-pointer items-center justify-between text-left text-base leading-5 font-bold uppercase lg:text-xl lg:leading-6"
          >
            CUSTOM INVESTIGATION UPLOADED
          </button>
          <div className="mt-6 flex flex-col gap-2.5">
            {formHandler?.values?.files?.map((file: any, index: number) => (
              <button
                className="border-brand-fair-orange hover:text-brand-byzantine hover:border-brand-byzantine text-neutral-tones-text font-inter-variable flex cursor-pointer items-center gap-2.5 rounded-md border p-15 text-sm leading-4 font-bold transition-all duration-300"
                key={index}
                type="button"
                onClick={() => {
                  const url = URL.createObjectURL(file);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = file.name || 'download';
                  document.body.appendChild(a);
                  a.click();
                  setTimeout(() => {
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                  }, 0);
                }}
              >
                <DownloadIcon className="h-5 w-5 [&>g>path]:fill-current" />{' '}
                {file?.name}
              </button>
            ))}
            {attachments?.map((file: any, index: number) => (
              <Link
                className="border-brand-fair-orange hover:text-brand-byzantine hover:border-brand-byzantine text-neutral-tones-text font-inter-variable flex cursor-pointer items-center gap-2.5 rounded-md border p-15 text-sm leading-4 font-bold transition-all duration-300"
                key={index}
                type="button"
                to={file?.url}
                target="_blank"
                rel="noreferrer noreferrer"
              >
                <DownloadIcon className="h-5 w-5 [&>g>path]:fill-current" />{' '}
                {file?.name}
              </Link>
            ))}
          </div>
        </div>
      )}
      <div className="border-neutral-tones-text/10 bg-card-backgrounds/5 rounded-10 mt-9 w-full border p-15 lg:p-5">
        <button
          type="button"
          onClick={() => setToggle(toggle === 'step-4' ? '' : 'step-4')}
          className="text-brand-papery flex w-full cursor-pointer items-center justify-between text-left text-base leading-5 font-bold uppercase lg:text-xl lg:leading-6"
        >
          ROOT CAUSE ANALYSIS
          <ChevronIcon
            className={clsx(
              'h-8 w-8 transition-all duration-300 lg:h-11 lg:w-11 [&>path]:fill-current',
              toggle === 'step-4' && 'rotate-180',
            )}
          />
        </button>
        {toggle === 'step-4' && (
          <div
            className={clsx(
              'mt-5 h-auto max-w-955 gap-9 overflow-hidden transition-all duration-300',
            )}
          >
            <p className="text-neutral-tones-text font-inter-variable text-lg leading-5 font-normal capitalize">
              {formHandler?.values?.rootCauseAnalysis || 'N/A'}
            </p>
          </div>
        )}
      </div>

      <div className="mt-9 flex items-center gap-8">
        <Button
          size={sm}
          otherClasses="w-full sm:w-185"
          variant="secondary"
          type="button"
          disable={formHandler?.isSubmitting}
          onClick={() => handleNextStep(4)}
        >
          <ChevronIcon className="h-6 w-6 rotate-90 [&>path]:fill-current" />
          Go Back
        </Button>
        <Button
          size={sm}
          otherClasses="w-full sm:w-185"
          type="submit"
          disable={formHandler?.isSubmitting || disbaleButtons}
          loading={formHandler?.isSubmitting}
        >
          Complete
          <CheckIcon className="h-6 w-6 [&>path]:fill-current" />
        </Button>
      </div>
    </div>
  );
};

export default InvestigationReportingStepFive;
